import ApiClient from '@package/services/src/ApiClient';
import ListrakAnalytic from '@package/services/src/ListrakAnalytic';
import Result, { EmptyResult } from '@package/services/src/Result';
import CookieStorage from '@package/services/src/Storage/Cookie';

import {
  AddMissingItemRequest,
  AddProductRequest,
  AddProductServiceRequest,
  DeleteProductRequest,
  DeleteProductServiceRequest,
  GetProductAddonsRequest,
  GetServiceInfoRequest,
  GetShoppingCardRequest,
  GetSuggestedProductsRequest,
  ProductQuantityRequest,
} from './interactorTypes';
import { CartService } from './services';
import {
  CartSummary,
  GetShareCartLinkResponse,
  ProductAddonElement,
  ProductAddons,
  ServiceInfo,
  ShoppingCard,
  SubOrder,
} from './types';

export default class CartApiService implements CartService {
  public constructor(
    private readonly apiClient: ApiClient,
    private readonly cookies: CookieStorage,
    private readonly listrak: ListrakAnalytic,
  ) {}

  public async getShoppingCard(request?: GetShoppingCardRequest): Promise<Result<ShoppingCard | undefined>> {
    const { data: orderWasSubmitted } = await this.cookies.get('order_submitted');

    if (orderWasSubmitted) {
      return new Result({
        adminUser: false,
        applyCouponMessage: null,
        enabledRecaptcha: false,
        notDeliverable: false,
        cartItems: 0,
        cartSummary: {} as CartSummary,
        subOrders: [] as Array<SubOrder>,
        regularCouponApplied: false,
        paymentMethod: '',
      });
    }

    try {
      const res = await this.apiClient.get('/shoppingCart', {
        credentials: request?.cookie ? 'include' : undefined,
        headers: request?.cookie ? { Cookie: request.cookie } : undefined,
      });

      try {
        const json: ShoppingCard = await res.json();
        const { data: cartItems } = await this.cookies.get('cartItems', request?.cookie);

        const cartData = {
          ...json,
          cartItems: cartItems ? parseInt(cartItems) : 0,
          cookies: res.headers.get('set-cookie'),
        };

        if (request?.mainCart) {
          void this.listrak.applyProducts({
            products: cartData.subOrders.flatMap((suborder) => suborder.products),
            total: cartData.cartSummary.totalDue.toString(),
          });
        }

        return new Result(cartData);
      } catch (error) {
        if (res.status === 200) {
          return new Result({
            adminUser: false,
            applyCouponMessage: null,
            enabledRecaptcha: false,
            notDeliverable: false,
            cartItems: 0,
            cartSummary: {} as CartSummary,
            subOrders: [] as Array<SubOrder>,
            regularCouponApplied: false,
            paymentMethod: '',
          });
        }
        throw new Error('cant get shopping cart data');
      }
    } catch (error) {
      throw new Error('cant get shopping cart data');
    }
  }

  public async synchronizationCookie(): Promise<EmptyResult> {
    try {
      await this.apiClient.get('/shoppingCart/syncCookies');
      return new EmptyResult();
    } catch (error) {
      return new EmptyResult();
    }
  }

  public async clearShippingOptions(): Promise<EmptyResult> {
    try {
      await this.apiClient.post('/shoppingCart/clear-shipping');
      return new EmptyResult();
    } catch (error) {
      return new EmptyResult();
    }
  }

  public async getCartShareLink(): Promise<Result<string | undefined>> {
    try {
      const res = await this.apiClient.get('/shoppingCart/sharedCartDetails');

      const link: GetShareCartLinkResponse = await res.json();

      return new Result(`?orderKey=${link.orderKey}&refId=${link.refId}`);
    } catch (error) {
      return new Result(undefined);
    }
  }

  public async getProductAddons(request: GetProductAddonsRequest): Promise<Result<ProductAddons | undefined>> {
    try {
      const { productSku } = request;
      const url = `/shoppingCart/addons?sku=${productSku}`;
      const res = await this.apiClient.get(url);
      const json = await res.json();

      return new Result(json);
    } catch (error) {
      console.error('Cant` get product addons data', error);
      return new Result(undefined);
    }
  }

  public async getSuggestedProducts(
    request: GetSuggestedProductsRequest,
  ): Promise<Result<Array<ProductAddonElement> | undefined>> {
    try {
      const { vendorId, minPrice, excludeIds } = request;
      const url = `/shoppingCart/suggested-products?vendorId=${vendorId}&targetMinPrice=${minPrice}&excludeSkus=${excludeIds}`;
      const res = await this.apiClient.get(url);
      const json = await res.json();
      return new Result(json);
    } catch (error) {
      console.error('Cant` get other products data', error);
      return new Result(undefined);
    }
  }

  public async getServiceInfo(request: GetServiceInfoRequest): Promise<Result<ServiceInfo | undefined>> {
    try {
      const { relatedProductSku, serviceId } = request;
      const url = `/shoppingCart/serviceInfo/${relatedProductSku}/${serviceId}`;
      const res = await this.apiClient.get(url);
      const json = await res.json();

      return new Result(json);
    } catch (error) {
      console.error('Cant` get service info data', error);
      return new Result(undefined);
    }
  }

  public async addProduct(request: Array<AddProductRequest>): Promise<EmptyResult> {
    const products = request.map(({ productSKU, quantity }) => ({
      subProductSku: productSKU,
      quantity: quantity || 1,
    }));

    await this.apiClient.post('/shoppingCart/products', {
      body: products,
    });

    return new EmptyResult();
  }

  public async addProductService(request: AddProductServiceRequest): Promise<EmptyResult> {
    const { productSku, serviceId, serviceType, productQuantity = 1 } = request;
    await this.apiClient.put('/shoppingCart/productService', {
      body: { productSku, serviceId, serviceType, productQuantity },
    });

    return new EmptyResult();
  }

  public async addMissingItem(request: AddMissingItemRequest): Promise<EmptyResult> {
    await this.apiClient.post('/shoppingCart/products/services/relatedProducts', {
      body: request,
    });

    return new EmptyResult();
  }

  public async updateProductQuantity(request: ProductQuantityRequest): Promise<EmptyResult> {
    const { productItemId, ...rest } = request;

    await this.apiClient.put(`/shoppingCart/products/${productItemId}`, {
      body: rest,
    });

    return new EmptyResult();
  }

  public async deleteProduct(request: DeleteProductRequest): Promise<EmptyResult> {
    const { productItemId, orderId } = request;

    await this.apiClient.delete(`/shoppingCart/products/${productItemId}`, {
      body: { orderId },
    });

    return new EmptyResult();
  }

  public async deleteProductService(request: DeleteProductServiceRequest): Promise<EmptyResult> {
    const { ...rest } = request;

    await this.apiClient.delete('/shoppingCart/products/services', {
      body: rest,
    });

    return new EmptyResult();

  }

  public async saveToWishlist(): Promise<EmptyResult> {
    await this.apiClient.post('/shoppingCart/wishList/products');

    return new EmptyResult();
  }
}
